import { Fragment, useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { filtersFromQuery, hasActiveFilters } from '../../utils/search-filter-state';
import { useProduct, useUI, useCategory, useRouterContext } from '../../contexts';
import { useSearch } from '../../contexts/SearchProvider';
import { Footer } from '../footer';
import Listings from '../legacy-listings';
import SearchViewToggle from '../search-view-toggle';
import SearchSortSelect from '../search-sort-select';
import { parseListingSort } from '../../utils/listing-sort';
import Showcase from '../showcase';
import GridSidebarAd from './GridSidebarAd';
import { getAnalytics } from '../../utils/analytics';
import { trackError, ErrorSeverity } from '../../utils/error-tracking';
import { bumpCrashContext } from '../../utils/tab-crash-sentinel';
import { SearchListingProps, ShowcaseGroup, HomeShowcase, ShowcaseData } from './types';
import {
  type TagShowcaseRow,
  groupRowSearchParams,
  seedGroupId,
  showcaseLoadMoreVisible,
  shownListingIds,
  tagRowProps,
  unrankedTagRows,
} from './showcase-rows';
import type { Product } from '../../utils/product/product-types';
import { SHOWCASES_PER_PAGE } from './constants';
import NoResultsMessage from './NoResultsMessage';
import ShowcaseRowSkeleton from './ShowcaseRowSkeleton';
import NearYouShowcase from './NearYouShowcase';
import ListingGridSkeleton from './ListingGridSkeleton';
import { useGridHoldOnBack } from './useGridHoldOnBack';
import BackToTopButton from './BackToTopButton';
import LoadMoreSection from './LoadMoreSection';
import { useLoadAhead } from './useLoadAhead';
import { useGridFeed } from './useGridFeed';
import { aggregateShowcaseData, sortShowcasesWithOtherAtEnd } from './aggregateShowcaseData';
import { useSearchApi } from './useSearchApi';
import { useHomeShowcases, defaultCategoryGroupOrder } from '../../hooks/useHomeShowcases';
import { getSessionInterest, sessionAffinityTags } from '../../utils/session-interest';
import { useHomeExtraRows } from '../../hooks/useHomeExtraRows';
import { buildHomeRows, type HomeRow } from '../../utils/home-row-rank';
import { getPageLoadShuffleSeed } from '../../utils/seeded-shuffle';
import { BudgetFindsShowcase, SpecialShowcaseRow } from '../foryou-rows';
import { recallPageState, rememberPageState } from '../../utils/browse-list-cache';
import { isBackNavigation } from '../../utils/navigation-direction';

/** Home's tag rows past the category groups, kept for Back (utils/browse-list-cache.ts). */
type KeptHomeTagRows = { rows: TagShowcaseRow[]; seenTags: string[]; mode: boolean };
const homeTagRowsKey = (sort?: string) => `home-tag-rows:${sort ?? ''}`;


const SearchListing = ({
  searchEmpty,
  heading,
  belowHeading,
  page: pageProp,
  setPage: setPageProp,
  done: doneProp,
  setDone: setDoneProp,
  focusedSubcategory,
  showcaseData,
  categoryGroupLabel,
  allCategoryGroups,
  isHome = false,
  parentCategorySlug,
  searchQuery,
  pageType,
  activeFilters,
  sortable = false,
  gridOnly = false,
}: SearchListingProps) => {
  const { setProduct, currentList, replaceCurrentList } = useProduct();
  const { searchParams } = useSearch();
  const { clientSide, searchViewMode, searchViewModeSettled } = useUI();
  const { getCategoryById } = useCategory();
  const { pathname, query: routerQuery } = useRouterContext();
  const { fetchSearchList, fetchHomeShowcases, fetchShowcaseListings, fetchTagFallbackList, fetchTagShowcases, pickDataForListing } = useSearchApi();
  const isSearchPage = pathname === '/search' || gridOnly;

  /**
   * The showcase fetchers return schema-validated search rows; ShowcaseGroup and
   * HomeShowcase describe the same rows as the Product this UI renders. They are
   * separate hand-written descriptions of one wire object -- contexts/
   * ProductProvider narrows the same boundary the same way -- so the narrowing
   * is stated here rather than left as an `any` on the fetcher's return type,
   * where it reached every caller instead of this one.
   */
  const asShowcaseData = (r: Awaited<ReturnType<typeof fetchShowcaseListings>>) =>
    r as unknown as ShowcaseData;

  const homeShowcasesNarrowed = useCallback(
    async (params: Parameters<typeof fetchHomeShowcases>[0]) =>
      (await fetchHomeShowcases(params)) as unknown as {
        showcases: HomeShowcase[];
        deferredGroups?: number[];
        groupOrder?: number[];
        groupScores?: Record<string, number>;
      },
    [fetchHomeShowcases],
  );

  // Use props or local state for pagination
  const [pageLocal, setPageLocal] = useState(1);
  const [doneLocal, setDoneLocal] = useState(false);
  const page = pageProp !== undefined ? pageProp : pageLocal;
  const setPage = setPageProp || setPageLocal;
  const done = doneProp !== undefined ? doneProp : doneLocal;
  const setDone = setDoneProp || setDoneLocal;

  // Home showcases are fetched client-side. `pages/index.tsx` no longer touches
  // them: an ISR-cached page cannot carry a per-buyer ordering.
  /**
   * The home row order, ranked by what this session has looked at.
   *
   * Computed with `useState`'s lazy initialiser so it is read from storage
   * SYNCHRONOUSLY, before the hook's first fetch -- a personalized first screen
   * therefore costs one round trip, not two. Frozen for the life of the mount
   * for the same reason the hook freezes its own copy: re-ranking mid-scroll
   * would move rows the buyer has already passed.
   *
   * A session with no signal yet gets the default order back unchanged, so this
   * is safe to apply unconditionally on a cold start.
   */
  const [homeGroupOrder] = useState<number[] | undefined>(() => {
    if (!isHome) return undefined;
    // The candidates. The server ranks them on the first request, with this
    // session's interest (below) as part of the affinity: utils/home-row-rank.ts.
    return allCategoryGroups ?? defaultCategoryGroupOrder();
  });
  /** This session's interest per group, sent with the ranking request. Read once, at mount. */
  const [homeInterest] = useState(() => (isHome ? getSessionInterest().categoryGroups : undefined));

  /**
   * The same vector, sent with every home-showcase request so the server
   * orders each row by overlap too -- rows lead with what this visit has
   * looked at, not just the groups. Read once, at mount, with the order.
   */
  const [homeAffinity] = useState(() => (isHome ? sessionAffinityTags() : undefined));

  // A picked sort (?sortBy=) is one list in one order: it only exists in the
  // grid, so it turns the showcase rows off on every page that offers it.
  // A picked sort (?sortBy=) orders each showcase row's listings; the rows
  // keep their ranked order. It no longer forces the grid (2026-10-14).
  const rowSort = sortable ? parseListingSort(routerQuery?.sortBy) : undefined;
  // Filters from the filter drawer are one list too: rows cannot carry them.
  const filtered = hasActiveFilters(filtersFromQuery((routerQuery ?? {}) as Record<string, never>));

  const home = useHomeShowcases({
    enabled: isHome && searchViewMode === 'showcase',
    groupOrder: homeGroupOrder,
    affinityTags: homeAffinity,
    rank: isHome,
    interest: homeInterest,
    sortBy: rowSort,
    fetchHomeShowcases: homeShowcasesNarrowed,
  });

  const homeShowcaseView = isHome && searchViewMode === 'showcase';
  /** For You's special rows and the visitor's tag rows, ranked in with the groups. */
  const homeExtras = useHomeExtraRows(homeShowcaseView);

  /**
   * Home's showcase fetch came back with nothing (it failed, or every group was
   * too thin). The page then falls back to the grid, fully: the grid list, which
   * is no longer fetched for showcase readers (useBrowseListings), is fetched
   * below, and load-more pages the grid rather than the showcase rows it no
   * longer shows.
   */
  const homeShowcaseCameBackEmpty =
    homeShowcaseView && searchViewModeSettled !== false && !home.isLoadingInitial && home.rows.length === 0;
  const homePagesShowcases = homeShowcaseView && !homeShowcaseCameBackEmpty;

  // Browse category showcase state. The page sends no rows, only their order
  // (pages/browse/[category]/index.tsx sends `fullShowcases: []`); they are
  // fetched below.
  const [loadedCategoryShowcases, setLoadedCategoryShowcases] = useState<ShowcaseGroup[]>([]);
  const [loadedSubcategoryIds, setLoadedSubcategoryIds] = useState<number[]>([]);

  /**
   * Take batches of a category group's subcategories until one yields a full
   * showcase row, or the cap is hit. One batch was not enough: a batch of three
   * thin subcategories appends nothing, and the reader saw a load-more that did
   * nothing. Bounded, so a group whose rest is all thin does not walk the whole
   * list at once; whatever is not reached stays untried for the next call.
   */
  const fetchCategoryShowcaseBatches = async (
    allSubcategoryIds: number[],
    catGroupId: number,
    tried: number[],
    existingCategoryIds: Set<number>,
  ): Promise<{ consumed: number[]; found: ShowcaseGroup[] }> => {
    const MAX_BATCHES = 4;
    const BATCH = 3;
    const consumed: number[] = [];
    const found: ShowcaseGroup[] = [];
    for (let batch = 0; batch < MAX_BATCHES && found.length === 0; batch++) {
      const next = allSubcategoryIds.filter((id) => !tried.includes(id) && !consumed.includes(id)).slice(0, BATCH);
      if (next.length === 0) break;
      const result = asShowcaseData(await fetchShowcaseListings({
        categoryGroupId: catGroupId,
        minProductsPerCategory: 7,
        categoryIds: next,
        ...(rowSort ? { sortBy: rowSort } : {}),
      }));
      consumed.push(...next);
      found.push(...result.fullShowcases.filter((s: ShowcaseGroup) => !existingCategoryIds.has(s.categoryId)));
    }
    return { consumed, found };
  };

  /**
   * A category page's showcase rows come from the browser. The server sends
   * only the subcategory order (`showcaseData.allSubcategoryIds`, no rows), so
   * the first rows are fetched here; see pages/browse/[category]/index.tsx.
   * `categoryShowcasesFor` is the showcaseData the rows were fetched for: until
   * it matches, the page holds a skeleton rather than painting the grid list
   * grouped into thin rows and then swapping them out.
   */
  const categoryShowcaseView =
    !isHome && !focusedSubcategory && !filtered && searchViewMode === 'showcase' &&
    !!showcaseData?.allSubcategoryIds?.length;
  const [categoryShowcasesFor, setCategoryShowcasesFor] = useState<{ data: ShowcaseData | undefined; sort: string } | null>(null);
  const categoryShowcasesPending =
    categoryShowcaseView && (categoryShowcasesFor?.data !== showcaseData || categoryShowcasesFor?.sort !== (rowSort ?? ''));

  // Other state
  const [loading, setLoading] = useState(false);

  /**
   * Load-more pages the grid unless a row view owns it (home rows, a category
   * page's subcategory rows). The grid's pages, then its tag-graph
   * recommendations, come from the shared pager.
   */
  const gridPaging =
    !homePagesShowcases &&
    !(searchViewMode === 'showcase' && !!showcaseData?.allSubcategoryIds?.length);
  const feed = useGridFeed({
    searchParams: searchParams as Record<string, unknown>,
    filtered,
    enabled: gridPaging,
    currentList,
    replaceCurrentList: replaceCurrentList as (list: Product[], params?: Record<string, unknown>) => void,
    fetchSearchList,
    fetchTagFallbackList,
    pickDataForListing,
  });

  // The page owns `page` and `done` for scroll restoration: the feed's count
  // is mirrored up for it to save, and a count it restores (Back after a
  // reload, with no list cache) is handed down, so the feed fills those pages
  // in order. Only the count on mount and a CHANGE in it are handed down: a
  // count left over from the last search must not walk the new one.
  const lastPageFromPageRef = useRef(0);
  useEffect(() => {
    if (!gridPaging) return;
    if (page !== lastPageFromPageRef.current) {
      lastPageFromPageRef.current = page;
      if (page > feed.page) feed.setPage(page);
      return;
    }
    if (feed.page > page) {
      lastPageFromPageRef.current = feed.page;
      setPage(feed.page);
    }
  }, [gridPaging, page, feed.page, feed.setPage, setPage]);
  // Up only: the page clears `done` itself on a new search, and a `done` it
  // restored on Back must stand.
  useEffect(() => {
    if (gridPaging && feed.done && !done) setDone(true);
  }, [gridPaging, feed.done, done, setDone]);

  // Tag-showcase continuation. Once the finite official category rows are
  // exhausted, we keep appending tag-titled showcase rows (seeded from the
  // group's tags, expanded via the embedding graph) so the stack never
  // dead-ends at "That's all folks!" until the graph is finally dry.
  //
  // On home, Back puts back the tag rows the reader had reached, with the
  // category rows above them (hooks/useHomeShowcases.ts).
  const [keptTagRows] = useState(() =>
    isHome && isBackNavigation() ? recallPageState<KeptHomeTagRows>(homeTagRowsKey(rowSort)) : null
  );
  const [tagShowcases, setTagShowcases] = useState<TagShowcaseRow[]>(() => keptTagRows?.rows ?? []);
  const [tagSeenTags, setTagSeenTags] = useState<string[]>(() => keptTagRows?.seenTags ?? []);
  const [tagShowcaseMode, setTagShowcaseMode] = useState(() => keptTagRows?.mode ?? false);
  /** The resets below run on mount too; a Back that put rows back skips them once. */
  const keepTagRowsOnMountRef = useRef(!!keptTagRows);
  const resetTagRows = () => {
    if (keepTagRowsOnMountRef.current) return;
    setTagShowcases([]);
    setTagSeenTags([]);
    setTagShowcaseMode(false);
  };
  useEffect(() => {
    if (!isHome || !tagShowcaseMode) return;
    rememberPageState<KeptHomeTagRows>(homeTagRowsKey(rowSort), { rows: tagShowcases, seenTags: tagSeenTags, mode: tagShowcaseMode });
  }, [isHome, rowSort, tagShowcases, tagSeenTags, tagShowcaseMode]);

  // Synchronous guard against overlapping load-more calls (state updates aren't
  // synchronous, so `loading` alone can race under rapid triggers).
  const isFetchingRef = useRef(false);

  // Best-effort set of listing ids already on screen, so appended tag rows do
  // not repeat them. Approximate: it sees each row's SEED listings, not however
  // far an individual row's Swiper has independently paged (the server also
  // excludes at ES time + dedups across rows, so residual overlap is minor).
  const TAG_SEEN_LISTING_CAP = 2000;
  // Home's rows live in the hook: reading only the category rows let tag rows
  // repeat listings the group rows above them already showed.
  const collectShownListingIds = (): number[] =>
    shownListingIds([...(isHome ? home.rows : []), ...loadedCategoryShowcases, ...tagShowcases], currentList);

  // Pull the next page of category-group-seeded tag showcases and append them as
  // new rows. Only declares the stack truly finished ("That's all folks!") once
  // the tag graph itself is dry (a page that yields no rows).
  const loadTagShowcases = async (seedCategoryGroupId?: number): Promise<void> => {
    const result = await fetchTagShowcases({
      categoryGroupId: seedCategoryGroupId,
      seenTags: tagSeenTags,
      seenListingIds: collectShownListingIds().slice(-TAG_SEEN_LISTING_CAP),
      limit: SHOWCASES_PER_PAGE,
      listingsPerTag: 12,
      minListingsPerTag: 7,
    });

    if (!tagShowcaseMode) setTagShowcaseMode(true);

    const rows: TagShowcaseRow[] = result.showcases.map((s) => ({
      tag: s.tag,
      title: s.title,
      listings: s.listings as Record<string, unknown>[],
      categoryGroupId: s.categoryGroupId,
      brand: s.brand,
    }));

    if (rows.length > 0) {
      setTagShowcases((prev) => [...prev, ...rows]);
      // Stateless server contract: accumulate the tags we have rendered so the
      // next page drifts onward instead of repeating them.
      setTagSeenTags((prev) => Array.from(new Set([...prev, ...rows.map((r) => r.tag)])));
    }

    if (!result.hasMore && rows.length === 0) {
      setDone(true);
    }
  };

  // Public trigger: a synchronous ref guard blocks overlapping calls before any
  // await, then delegates to the (unchanged) body. The ref is reset once the
  // whole run settles.
  const loadMore = async () => {
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;
    try {
      await runLoadMore();
    } finally {
      isFetchingRef.current = false;
    }
  };

  // No `loading` or `fetching` check here: loadMore's ref already refuses a
  // second run while one is out, and both are only true inside a run, so a call
  // that saw them true was reading a finished run's render.
  const runLoadMore = async () => {
    setLoading(true);
    // Carried on the crash heartbeat: how deep the reader was when a tab died.
    bumpCrashContext('load_mores');

    const analytics = getAnalytics();

    // Home page showcases: owned by useHomeShowcases, which fetches them
    // client-side and appends. Appending rather than re-sorting is deliberate --
    // a re-sort would move rows the buyer has already scrolled past.
    if (homePagesShowcases) {
      analytics?.loadMore({
        pageType: 'browse_home',
        contentType: 'showcases',
        currentPage: Math.floor(home.fetchedGroupCount / SHOWCASES_PER_PAGE),
        itemsLoaded: home.rows.length,
        totalItemsAvailable: home.totalGroupCount,
      });

      if (home.hasMore) {
        const appended = await home.loadMore();
        // The hook walks past groups the server drops, but that walk is bounded
        // so a long enough run of thin ones can still end a click with nothing.
        // Fall through rather than leave a button that looks broken -- tag
        // showcases are where this path ends up anyway.
        if (appended === 0) await loadTagShowcases(undefined);
      } else {
        // Official category groups exhausted -> keep filling with tag showcases.
        // Home seeds from all groups (popularity order); undefined = no scoping.
        await loadTagShowcases(undefined);
      }

      setLoading(false);
      return;
    }

    // Category page showcase loading. Never while the first rows are still on
    // their way: they count as loading (useLoadAhead below), and the button is
    // not shown.
    if (showcaseData?.allSubcategoryIds && showcaseData.allSubcategoryIds.length > 0 && searchViewMode === 'showcase') {
      const allSubcategoryIds = showcaseData.allSubcategoryIds;
      const catGroupId = seedGroupId(showcaseData.categoryGroupId, searchParams.categoryGroupId);

      analytics?.loadMore({
        pageType: 'browse_category',
        contentType: 'showcases',
        categoryGroupId: catGroupId,
        itemsLoaded: loadedCategoryShowcases.length,
        totalItemsAvailable: allSubcategoryIds.length,
      });

      if (allSubcategoryIds.some((id) => !loadedSubcategoryIds.includes(id))) {
        try {
          // Walks past thin subcategories; see fetchCategoryShowcaseBatches.
          // At least one subcategory is untried, so `consumed` is never empty.
          const { consumed, found } = await fetchCategoryShowcaseBatches(
            allSubcategoryIds,
            catGroupId as number,
            loadedSubcategoryIds,
            new Set(loadedCategoryShowcases.map((s) => s.categoryId)),
          );

          setLoadedCategoryShowcases(sortShowcasesWithOtherAtEnd([...loadedCategoryShowcases, ...found]));
          setLoadedSubcategoryIds([...loadedSubcategoryIds, ...consumed]);

          // Every remaining subcategory was thin. Rather than leave a button
          // that appears to do nothing, move straight on to tag showcases,
          // which is where this path ends up anyway.
          if (found.length === 0 && loadedSubcategoryIds.length + consumed.length >= allSubcategoryIds.length) {
            await loadTagShowcases(catGroupId);
          }
        } catch (error) {
          trackError('Error fetching more category showcases', ErrorSeverity.WARNING, { component: 'SearchListing', extra: { error: String(error) } });
        }
      } else {
        // All subcategories shown -> continue with this group's tag showcases.
        await loadTagShowcases(catGroupId);
      }

      setLoading(false);
      return;
    }

    // Default: load more listings
    analytics?.loadMore({
      pageType,
      contentType: 'listings',
      searchQuery,
      activeFilters,
      currentPage: page,
      itemsLoaded: currentList.length,
    });

    feed.loadNext();
    setLoading(false);
  };

  // Reset page (and any active tag-fallback) on search/category change. This
  // component persists across navigation, so a new search must clear fallback
  // mode or the next list would keep recommending from the old context.
  useEffect(() => {
    setPage(1);
    // Clear the tag-showcase continuation, or the new context would inherit
    // the previous one's appended rows. (The grid's own recommendations reset
    // with its search, in useGridFeed.)
    resetTagRows();
  }, [searchParams.q, searchParams.categoryGroupId, setPage]);

  // This component persists across navigation (it's not remounted
  // category->category), so a new category page RESETS the rows: otherwise the
  // previous page's showcases stay on screen (stale-listings, showcase-view
  // variant). The effect below then fetches the new page's first rows.
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- this component persists across category changes and must reset its rows when a new page's showcaseData arrives
    setLoadedCategoryShowcases([]);
    setLoadedSubcategoryIds([]);
    setDone(false);
    resetTagRows();
  }, [showcaseData, setDone]);

  // The first showcase rows of a category page. Declared after the sync above,
  // which empties the rows on a new showcaseData; this then fills them.
  useEffect(() => {
    if (!categoryShowcasesPending || !showcaseData?.allSubcategoryIds) return undefined;
    let cancelled = false;
    const catGroupId = seedGroupId(showcaseData.categoryGroupId, searchParams.categoryGroupId) as number;
    void fetchCategoryShowcaseBatches(showcaseData.allSubcategoryIds, catGroupId, [], new Set())
      .then(({ consumed, found }) => {
        if (cancelled) return;
        setLoadedCategoryShowcases(sortShowcasesWithOtherAtEnd(found));
        setLoadedSubcategoryIds(consumed);
      })
      .catch((error: unknown) => {
        trackError('Error fetching category showcases', ErrorSeverity.WARNING, { component: 'SearchListing', extra: { error: String(error) } });
      })
      .finally(() => {
        // On failure too: the page then shows the grid rather than a skeleton forever.
        if (!cancelled) setCategoryShowcasesFor({ data: showcaseData, sort: rowSort ?? '' });
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one fetch per showcaseData and sort; the fetcher and searchParams are read at call time
  }, [categoryShowcasesPending, showcaseData, rowSort]);

  // After every reset above has had its mount run: from here a reset is real.
  useEffect(() => {
    keepTagRowsOnMountRef.current = false;
  }, []);

  // Aggregate showcase data
  const showcasesToDisplay = useMemo(
    () =>
      aggregateShowcaseData({
        // On home these come from the client-side hook; elsewhere from the
        // lazily-loaded prop state.
        loadedShowcases: isHome ? home.rows : [],
        loadedCategoryShowcases,
        showcaseData,
        categoryGroupLabel,
        // Never on home's showcase view. Its rows come only from
        // useHomeShowcases; grouping the grid list by subcategory here painted
        // rows of one to five listings, which the real rows then replaced with
        // new titles -- "rows with not enough products, switched right after".
        // With nothing to group, the skeleton holds until the real rows land.
        currentList: homeShowcaseView || categoryShowcasesPending ? [] : currentList,
        getCategoryById,
        /*
          On home the hook IS the pager: `useHomeShowcases.loadMore()` appends
          exactly one page of rows to `home.rows`, so that array is already
          "what should be on screen". Slicing it again to `visibleShowcaseCount`
          made a second, stale gate — its only setter runs off the
          `homeShowcases` PROP, which home stopped passing when the fetch moved
          client-side, so the count sat at its initial 4 forever and every
          appended row was cut off. The button then looked dead for the first
          clicks and only "worked" once the hook ran out of category groups and
          fell through to tag rows, which render outside this slice.
        */
        visibleShowcaseCount: home.rows.length,
      }),
    [
      currentList,
      homeShowcaseView,
      categoryShowcasesPending,
      showcaseData,
      categoryGroupLabel,
      isHome,
      home.rows,
      loadedCategoryShowcases,
      getCategoryById,
    ]
  );

  type SpecialRow = NonNullable<ReturnType<typeof homeExtras.getShowcase>>;
  type HomeTagRow = (typeof homeExtras.tagRows)[number];

  /**
   * Home's page order: group rows, special rows and tag rows ranked together,
   * the first screen by score and later group pages appended
   * (utils/home-row-rank.ts). Empty until everything the first screen ranks
   * has arrived, so no row is painted and then pushed down.
   */
  const homeRows = useMemo((): Array<HomeRow<ShowcaseGroup, SpecialRow, HomeTagRow>> => {
    if (!homeShowcaseView || home.isLoadingInitial || !homeExtras.ready) return [];
    // A row under 4 listings is not shown, so it is not ranked either.
    const one = (type: SpecialRow['type']) => {
      const row = homeExtras.getShowcase(type);
      return row && row.listings.length >= 4 ? row : null;
    };
    return buildHomeRows<ShowcaseGroup, SpecialRow, HomeTagRow>({
      groupRows: showcasesToDisplay.map((row) => ({
        row,
        id: row.categoryId,
        score: home.groupScores[String(row.categoryId)] ?? 0,
      })),
      firstPageGroupCount: home.firstPageCount,
      specials: {
        recently_viewed: one('recently_viewed'),
        your_wishlist: one('your_wishlist'),
        recent_searches: one('recent_searches'),
        similar_to_liked: homeExtras.getShowcases('similar_to_liked'),
        similar_to_purchased: homeExtras.getShowcases('similar_to_purchased'),
        holiday: one('holiday'),
        trending: one('trending'),
        new_arrivals: one('new_arrivals'),
        best_deals: one('best_deals'),
      },
      selfRows: ['near_you', 'budget'],
      tagRows: homeExtras.tagRows,
      tagOf: (row) => row.tag,
      specialKeyOf: (kind, row, i) => `${kind}-${row.sourceListingId ?? i}`,
      seed: getPageLoadShuffleSeed(),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- getShowcase(s) change with the specials they read
  }, [homeShowcaseView, home.isLoadingInitial, home.groupScores, home.firstPageCount, homeExtras.ready, homeExtras.tagRows, homeExtras.getShowcase, showcasesToDisplay]);

  // What loads append to, and nothing derived from it: the display grouping
  // can shrink when home's first rows replace the grouped grid list.
  const shownCount =
    home.rows.length +
    loadedCategoryShowcases.length +
    tagShowcases.length +
    currentList.length;
  // The next page loads ahead, the same way on every grid page.
  const shownLoading = loading || (gridPaging && feed.loading);
  const shownDone = gridPaging ? feed.done || done : done;
  // A category page's first rows count as loading: a load-more then would fetch them twice.
  const { ref, loadMore: loadAhead } = useLoadAhead({
    onLoadMore: loadMore,
    loading: shownLoading || categoryShowcasesPending,
    done: shownDone,
    shownCount,
  });

  /**
   * Home holds the showcase layout while its first page is in flight.
   *
   * Without that clause, client-only fetching paints the EMPTY GRID first --
   * no rows and no list on the first frame -- and then hard-swaps the whole
   * subtree to the showcase layout when rows land. That is a full-page reflow
   * on every home visit, and precisely what moving the fetch client-side is
   * supposed to avoid.
   */
  const homeIsResolvingShowcases = homeShowcaseView && (home.isLoadingInitial || !homeExtras.ready);

  const gridFallbackFetchedRef = useRef(false);
  useEffect(() => {
    if (!homeShowcaseCameBackEmpty || gridFallbackFetchedRef.current || currentList.length > 0) return;
    gridFallbackFetchedRef.current = true;
    const listParams = { ...searchParams, page: 1 };
    void fetchSearchList(listParams).then((list) => {
      replaceCurrentList(pickDataForListing(list, 32) as unknown as Product[], listParams as Parameters<typeof replaceCurrentList>[1]);
    });
  }, [homeShowcaseCameBackEmpty, currentList, searchParams, fetchSearchList, replaceCurrentList, pickDataForListing]);

  /**
   * Grid view with nothing to show yet, and no answer from the search API. See
   * the placeholder's own note below for why `searchEmpty` is the signal.
   */
  const gridIsResolving = currentList.length === 0 && !searchEmpty;

  /** Skeleton tiles that keep the grid as tall as the reader left it, on Back. */
  const backHoldTiles = useGridHoldOnBack(currentList.length, done);
  const gridSkeletonTiles = Math.max(gridIsResolving ? 12 : 0, backHoldTiles);

  const showGridView =
    filtered ||
    focusedSubcategory ||
    searchViewMode === 'grid' ||
    isSearchPage ||
    (showcasesToDisplay.length === 0 && !homeIsResolvingShowcases && !categoryShowcasesPending);


  /** A category or group row. On home a row is a GROUP; everywhere else a single category. */
  const renderGroupRow = (group: ShowcaseGroup) => (
    <Showcase
      title={group.categoryName}
      searchParams={groupRowSearchParams(isHome, group.categoryId, rowSort)}
      initialListings={group.listings as unknown as Record<string, unknown>[]}
      categorySlug={group.categorySlug}
      parentCategorySlug={parentCategorySlug}
      listName={`category-${group.categoryId}`}
      itemsPerPage={10}
      recommendForever
      notInterestedSource={isHome ? 'home' : 'browse'}
      windowed
    />
  );

  const renderTagRow = (row: { tag: string; title?: string; brand?: string; categoryGroupId?: number; listings: unknown[] }) => (
    <Showcase
      {...tagRowProps(row, rowSort)}
      initialListings={row.listings as unknown as Record<string, unknown>[]}
      listName={`tag-${row.tag}`}
      tag={row.tag}
      itemsPerPage={10}
      notInterestedSource={isHome ? 'home' : 'browse'}
      windowed
    />
  );

  /** One of home's ranked rows (utils/home-row-rank.ts). */
  const renderHomeRow = (row: HomeRow<ShowcaseGroup, SpecialRow, HomeTagRow>) => {
    switch (row.kind) {
      case 'group':
        return renderGroupRow(row.row);
      case 'tag':
        return renderTagRow(row.row);
      case 'special':
        return (
          <SpecialShowcaseRow
            title={row.row.title}
            listings={row.row.listings}
            listName={row.key}
          />
        );
      case 'self':
        return row.self === 'near_you' ? (
          <NearYouShowcase />
        ) : (
          <div className="mb-10">
            <div className="browse-heading-row">
              <h2 className="browse-heading">Great Finds Under $100</h2>
            </div>
            <div className="listing-slider-container">
              <BudgetFindsShowcase />
            </div>
          </div>
        );
    }
  };

  return (
      <div className="search-listings" data-selector="browse" data-testid="search-listings">
        <div>
          <div
            className="browse-heading-row"
          >
            {searchEmpty && searchParams.q ? (
              <h1 className="search-heading" data-testid="no-results-heading">
                <NoResultsMessage query={searchParams.q} categoryGroupId={searchParams.categoryGroupId} searchParams={searchParams as unknown as Record<string, unknown>} />
              </h1>
            ) : (
              heading || ''
            )}
            <div className="browse-heading-controls">
              {sortable && <SearchSortSelect />}
              {!focusedSubcategory && !isSearchPage && <SearchViewToggle />}
            </div>
          </div>
          {belowHeading}

          {showGridView ? (
            <>
              {/*
                The grid is the DEFAULT view (`searchViewMode` starts at
                'grid'), and on any page whose list is fetched client-side —
                home, and every category page with no server list, which is all
                of them on Capacitor — it was empty for the first second and
                then popped its tiles in. `searchEmpty` is what separates "still
                waiting" from "genuinely nothing here": `getFreshList` sets it
                once the fetch has answered, so an empty list without it means
                the answer has not arrived.
              */}
              <div className="listing-cont" data-testid="listing-grid">
                <Listings
                  listName="currentList"
                  positionOffset={0}
                  list={currentList}
                  setProduct={setProduct}
                  max={99999}
                  clientSide={clientSide}
                  searchQuery={searchQuery}
                  pageType={pageType}
                  activeFilters={activeFilters}
                />
              </div>
              {/*
                After the tiles, so on Back the real ones take the skeletons'
                places one for one and the page never gets shorter than the
                reader left it (useGridHoldOnBack).
              */}
              {gridSkeletonTiles > 0 ? <ListingGridSkeleton tiles={gridSkeletonTiles} /> : null}
              {currentList.length > 0 && (
                <LoadMoreSection done={shownDone} loading={shownLoading} onLoadMore={loadAhead} refCallback={ref} />
              )}
              <BackToTopButton />
            </>
          ) : (
            <div className="browsing-cont" data-testid="showcase-view">
              {/* Holds the page's shape while the first client fetch resolves,
                  so the layout never collapses to the grid and swap back. */}
              {(homeIsResolvingShowcases || categoryShowcasesPending) && showcasesToDisplay.length === 0 && <ShowcaseRowSkeleton />}

              {/* A focused subcategory always shows the grid (showGridView), so
                  this view never has one. */}
              {homeShowcaseView
                ? homeRows.map((row) => <Fragment key={row.key}>{renderHomeRow(row)}</Fragment>)
                : showcasesToDisplay.map((group) => <Fragment key={group.categoryId}>{renderGroupRow(group)}</Fragment>)}

              {/* Tag-showcase continuation: keeps appending tag-titled rows once
                  the official category rows are exhausted. Each row is itself
                  infinite via recommendForever. */}
              {/* A tag already ranked onto home's first screen is not repeated. */}
              {unrankedTagRows(tagShowcases, homeShowcaseView, homeExtras.tagRows).map((row) => (
                <Fragment key={`tag-${row.tag}`}>{renderTagRow(row)}</Fragment>
              ))}

              {showcasesToDisplay.length === 0 && tagShowcases.length === 0 && !homeIsResolvingShowcases && !categoryShowcasesPending && (
                <div className="text-center py-12">
                  <p>No listings available</p>
                </div>
              )}

              {/* Home's showcase view pages with useHomeShowcases, so it waits
                  for real rows: keyed on the grid list, this mounted with the
                  thin grouped rows, sat inside its 1500px band, and fired a
                  load-more before the first real page had even arrived. */}
              {showcaseLoadMoreVisible({
                homeShowcaseView,
                homeRowCount: home.rows.length,
                tagRowCount: tagShowcases.length,
                tagShowcaseMode,
                listCount: currentList.length,
                categoryShowcasesPending,
              }) && (
                  <LoadMoreSection
                    done={shownDone}
                    loading={shownLoading}
                    onLoadMore={loadAhead}
                    refCallback={ref}
                  />
                )}
              <BackToTopButton className="px-6" />
            </div>
          )}
          <Footer />
        </div>

        <GridSidebarAd />
      </div>
  );
};

export default SearchListing;
